import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAuthStore } from '@auth/authStore';
import { useUrlAssetScope } from '@hooks';
import { PageTabs } from '@components';
import {
  inspectionTabs, readInspectionView, inspectionScopeKey,
  readInspectionSelection, changeInspectionView, changeInspectionSelection,
} from './inspection.navigation';
import MfsDraftWorkspace from '@/features/mfs-drafts/MfsDraftWorkspace';
import InspectionPlanningViews from '@/features/inspection-planning/InspectionPlanningViews';
import { previewItemsQuery } from '@/features/inspection-planning/planning.model';
import styles from './InspectionSuite.module.css';

function CompanyInspectionSuite({ companyId }) {
  const { siteId, assetId } = useUrlAssetScope();
  const [params, setParams] = useSearchParams();
  const [drafts, setDrafts] = useState([]);
  const [editor, setEditor] = useState(null);
  const view = readInspectionView(params);
  const scopeKey = inspectionScopeKey(companyId, siteId, assetId);
  const selection = readInspectionSelection(params, scopeKey);
  const hasSelection = params.has('inspectionCampaign') || params.has('inspectionItem');
  const selectionInScope = params.get('inspectionScope') === scopeKey;

  useEffect(() => {
    if (params.get('inspectionView') === view && !params.has('tab')) return;
    setParams(previous => changeInspectionView(previous, view), { replace: true });
  }, [params, view, setParams]);

  useEffect(() => {
    if (!drafts.length) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [drafts.length]);

  function save(snapshot) {
    setDrafts(previous => [...previous.filter(item => item.id !== snapshot.id), snapshot]);
  }
  function edit(snapshot) {
    setEditor({ initial: snapshot });
    setParams(previous => {
      const next = changeInspectionView(previous, 'schedules');
      next.delete('plan.item'); next.delete('plan.scope');
      return next;
    });
  }
  function preview(selection = {}) {
    setParams(previous => previewItemsQuery(previous, selection));
  }

  return <section className={styles.page}>
    <header className={styles.heading}><span>INSPECTION SUITE</span><h1>Maintenance & inspection</h1>
      <p>Draft maintenance scope and review proposed component work. Operational inspection records are not connected yet.</p></header>
    <div className={styles.context}>
      <strong>{assetId ? 'Asset scope' : siteId ? 'Site scope' : 'Fleet scope'}</strong>
      <span>{siteId ? 'Site: ' + siteId : 'All sites'}{assetId ? ' · Asset: ' + assetId : ''}</span>
      <span>{drafts.length} local draft(s) · Export JSON before leaving this page</span>
    </div>
    <PageTabs id="inspection" tabs={inspectionTabs} current={view} label="Inspection suite views"
      onChange={nextView => setParams(previous => changeInspectionView(previous, nextView))} />
    {hasSelection && <div className={styles.notice} role="status">
      <p>{selectionInScope ? 'Requested campaign: ' + (selection.campaignId || 'none') + ' · Item: ' + (selection.itemId || 'none') +
        '. Real inspection selection cannot be verified until the backend is connected.' :
        'The saved inspection selection belongs to another location scope and is not applied here.'}</p>
      <button type="button" className={styles.button}
        onClick={() => setParams(previous => changeInspectionSelection(previous, scopeKey, { view }))}>Clear inspection selection</button>
    </div>}
    {inspectionTabs.map(tab => <section key={tab.id} id={'inspection-panel-' + tab.id}
      role="tabpanel" aria-labelledby={'inspection-tab-' + tab.id} hidden={view !== tab.id}
      tabIndex={0} className={styles.panel}>
      {tab.id === 'schedules' ?
        <MfsDraftWorkspace companyId={companyId} siteId={siteId} assetId={assetId}
          drafts={drafts} onSave={save} editor={editor} onEditorChange={setEditor} onPreview={preview} /> :
        view === tab.id && <InspectionPlanningViews view={view} companyId={companyId} siteId={siteId} assetId={assetId}
          drafts={drafts} onEdit={edit} onPreview={preview}
          onSchedules={() => setParams(previous => changeInspectionView(previous, 'schedules'))} />}
    </section>)}
  </section>;
}

export default function InspectionSuite() {
  const companyId = useAuthStore(state => state.companyID);
  return companyId ? <CompanyInspectionSuite key={String(companyId)} companyId={companyId} /> :
    <section className={styles.page}><p className={styles.notice}>Select a company to draft maintenance work.</p></section>;
}